import React, { Component, ReactNode } from 'react';
import CaseChartIndicatorComponent from '../widget/CaseChartIndicatorComponent';
import { CaseSettings } from '../../../../domain/case/CaseDomains';
import { TransactionData, TransactionRiskType } from '../../../../domain/stream/transaction/TransactionDomains';
import CaseRadialProgressContainer from '../../../custom/radial-progress-bar/CaseRadialProgressContainer';
import ValueUtil from '../../../../util/ValueUtil';
import { RootState } from '../../../../redux/store';
import { User } from '../../../../domain/rbac/RbacDomains';
import { connect } from 'react-redux';
import { ConnectedProps } from 'react-redux/es/exports';
import LanguageUtil from '../../../../util/LanguageUtil';

class CaseDetailIndicatorPanelComponent extends Component<Props> {
  public state = new State();

  public render(): ReactNode {
    const { transactionData, user, scores, alarmDomain, riskType, activeRuleCount } = this.props;
    const { progressColor, labelColor } = this.state;

    return (
      <div
        className={
          'grid grid-cols-1 lg:grid-cols-3 xl:grid-cols-5 gap-y-4  gap-x-5 px-4 py-4 w-full h-full bg-[#F9FAFB] border border-[#E0E4E8] rounded-[12px]'
        }
      >
        <div className={'flex col-span-1 justify-center w-full h-full'}>
          <CaseRadialProgressContainer
            header={LanguageUtil.get({ id: 'pages.case.detail.indicators.transactionScore.title', defaultMessage: 'Transaction Score' })}
            subHeader={LanguageUtil.get(
              {
                id: 'pages.case.detail.indicators.transactionScore.description',
                defaultMessage: `Sum of ${activeRuleCount} rule  scores`,
              },
              { 0: `${activeRuleCount}` },
            )}
            score={Number(transactionData?.result[riskType].score)}
            progressColor={progressColor}
            labelColor={labelColor}
            loading={transactionData?.result[riskType].score == undefined}
            maxScore={activeRuleCount * 100}
          />
        </div>
        <div className={'flex col-span-1  justify-center w-full h-full'}>
          <CaseRadialProgressContainer
            header={LanguageUtil.get({
              id: 'pages.case.detail.indicators.transactionVelocityScore.title',
              defaultMessage: 'Transaction Velocity Score',
            })}
            subHeader={LanguageUtil.get(
              {
                id: 'pages.case.detail.indicators.transactionVelocityScore.description',
                defaultMessage: `Sum of ${scores?.caseCount || 0} rule scores`,
              },
              { 0: `${scores?.caseCount || 0}` },
            )}
            score={Math.round(scores?.avgTransactionScore || 0)}
            progressColor={progressColor}
            loading={scores?.avgTransactionScore == undefined && Object.keys(scores).length > 0}
            labelColor={labelColor}
            maxScore={activeRuleCount * 100}
          />
        </div>
        <div className={'flex col-span-1 center justify-center w-full h-full'}>
          <CaseRadialProgressContainer
            header={LanguageUtil.get(
              {
                id: 'pages.case.detail.indicators.fraudVelocityScore.title',
                defaultMessage: 'Transaction Velocity Score',
              },
              { riskType: user.activeRiskType == 'fraud' ? 'Fraud' : 'Incompliant' },
            )}
            subHeader={LanguageUtil.get(
              {
                id: 'pages.case.detail.indicators.fraudVelocityScore.description',
                defaultMessage: `Average of ${scores?.totalFraudulentTransactionCount || 0} ${riskType == 'fraud' ? 'fraudulent' : 'incompliant'} transaction score`,
              },
              {
                0: scores?.totalFraudulentTransactionCount || 0,
                riskType: user.activeRiskType == 'fraud' ? 'Fraudulent' : 'Incompliant',
              },
            )}
            key={scores?.avgFraudulentScore + scores?.totalFraudulentTransactionCount + scores?.avgTransactionScore}
            score={Math.round(scores?.avgFraudulentScore || 0)}
            progressColor={Math.round(scores?.avgTransactionScore || 0) == 0 ? 'gradient-low' : 'gradient-high'}
            labelColor={Math.round(scores?.avgTransactionScore || 0) == 0 ? 'text-[#7ECC8B]' : 'text-[#D23531]'}
            loading={scores?.avgFraudulentScore == undefined && Object.keys(scores).length > 0}
            maxScore={activeRuleCount * 100}
          />
        </div>
        <div className={'flex flex-col col-span-1 lg:col-span-3 xl:col-span-1 gap-y-4'}>
          <CaseChartIndicatorComponent
            score={`${ValueUtil.parseNumber(scores?.totalTransactionCount || 0)} Transaction`}
            label={LanguageUtil.get({
              id: 'pages.case.detail.indicators.totalTransactionCount.title',
              defaultMessage: 'Transaction Score',
            })}
          />
          <CaseChartIndicatorComponent
            currency={alarmDomain?.transactionCurrencyField}
            score={ValueUtil.parseNumber(scores?.sumTransactionAmountForResponse || 0)}
            label={LanguageUtil.get({
              id: 'pages.case.detail.indicators.totalTransactionAmount.title',
              defaultMessage: 'Total Transaction Amount',
            })}
          />
        </div>
        <div className={'flex flex-col col-span-1 lg:col-span-3 xl:col-span-1 gap-y-4'}>
          <CaseChartIndicatorComponent
            score={`${ValueUtil.parseNumber(scores?.totalFraudulentTransactionCount || 0)} ${user.activeRiskType == 'fraud' ? 'Fraud' : 'Incompliant'} / ${ValueUtil.parseNumber(scores?.caseCount || 0)} Suspicious`}
            label={this.getRatioText()}
          />
          <CaseChartIndicatorComponent
            currency={alarmDomain?.transactionCurrencyField}
            score={ValueUtil.parseNumber(scores?.sumTransactionAmountForFraud || 0)}
            label={LanguageUtil.get({
              id: 'pages.case.detail.indicators.totalFraudAttemptCost.title',
              defaultMessage: 'Total Fraud Attempt Cost',
            })}
          />
        </div>
      </div>
    );
  }

  public componentDidMount() {
    const { transactionData, riskType } = this.props;
    const rules = transactionData?.result[riskType].rules;
    if (rules) {
      if (rules.filter((rule) => rule.score == 100).length > 0) {
        this.setState({
          progressColor: 'gradient-high',
          labelColor: 'text-[#D23531]',
        });
      } else if (rules?.filter((rule) => rule.score != 100 && rule.score > 0 && rule.score < 100)) {
        this.setState({
          progressColor: 'gradient-medium',
          labelColor: 'text-[#F19E46]',
        });
      } else {
        this.setState({
          progressColor: 'gradient-low',
          labelColor: 'text-[#7ECC8B]',
        });
      }
    }
  }

  private getRatioText = () => {
    const { user } = this.props;
    switch (user.activeRiskType) {
      case 'fraud':
        return LanguageUtil.get(
          {
            id: 'pages.case.detail.indicators.fraudSuspiciousRatio.title',
            defaultMessage: 'Fraudulent / Suspicious ratio',
          },
          { 0: 'Fraudulent' },
        );
      case 'compliance':
        return LanguageUtil.get(
          {
            id: 'pages.case.detail.indicators.fraudSuspiciousRatio.title',
            defaultMessage: 'Fraudulent / Suspicious ratio',
          },
          { 0: 'Incompliant' },
        );
      default:
        return 'N/A';
    }
  };
}

interface Props {
  transactionData: TransactionData | undefined;
  alarmDomain: CaseSettings | undefined;
  scores: Record<string, number>;
  activeRuleCount: number;
  riskType: TransactionRiskType;
}

class State {
  progressColor: string = 'gradient-low';
  labelColor: string = 'text-[#7ECC8B]';
}

interface Props extends ReduxProps {}

const mapStateToProps = (state: RootState) => ({
  user: state.auth.user as User,
});
const connector = connect(mapStateToProps, {});
type ReduxProps = ConnectedProps<typeof connector>;

export default connector(CaseDetailIndicatorPanelComponent);
